import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileText, ShieldCheck, Clock, Download } from "@phosphor-icons/react";

/**
 * HabitabilityCard — per-WO "Closing Summary → 1-2 page PDF" tool.
 * The generated PDF is pinned to the WO and copied to the property
 * record so L&I / internal audits can pull the habitability trail later.
 */
export default function HabitabilityCard({ jobId, initialSummary = "" }) {
  const [summary, setSummary] = useState(initialSummary);
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const resolveUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("http")) return u;
    const base = API.replace(/\/api$/, "");
    return `${base}${u.startsWith("/") ? "" : "/"}${u}`;
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/jobs/${jobId}/habitability-pdf`);
        if (!alive) return;
        setSummary(r.data.closing_summary || initialSummary || "");
        setLatest(r.data.latest || null);
        setHistory(r.data.history || []);
      } catch { /* endpoint may 404 for a brand-new WO */ }
      finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const generate = async () => {
    if (!summary.trim()) {
      toast.error("Add a closing summary first");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/habitability-pdf`, {
        closing_summary: summary.trim(),
        save_to_property: true,
      });
      const rec = r.data.record;
      setLatest(rec);
      setHistory((h) => [rec, ...h]);
      toast.success(r.data.saved_to_property
        ? "Habitability PDF generated · saved to property record"
        : "Habitability PDF generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "PDF generation failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="mt-6 rugged-card p-4 border-l-4 border-indigo-500 bg-indigo-50/40"
      data-testid="habitability-pdf-card">
      <div className="flex items-start gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <ShieldCheck weight="fill" size={16} className="text-indigo-600"/>
          <div className="font-display font-black text-sm uppercase tracking-wider text-indigo-900">
            Closing Summary & Habitability PDF
          </div>
        </div>
        {latest && (
          <span className="ml-auto px-2 py-0.5 bg-emerald-600 text-white font-black uppercase text-[9px] tracking-widest rounded-sm">
            ✓ On record · {(latest.generated_at || "").slice(0, 10)}
          </span>
        )}
      </div>
      <p className="text-[11px] text-slate-600 mt-1">
        Write a short 1-2 paragraph closing note. A 1-2 page PDF (photos + description + your summary) will pin to the WO and drop into the property record for L&amp;I / habitability audits.
      </p>

      <textarea
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        data-testid="habitability-summary-textarea"
        rows={4}
        placeholder="e.g. Water heater replaced with new 50gal Rheem unit. Pressure-tested at 60 PSI. Cold-water shut-off replaced. Tenant confirms hot water restored. No further leaks observed after 48hr recheck."
        className="mt-2 w-full border-2 border-indigo-300 rounded p-2 text-sm font-medium focus:border-indigo-600 focus:outline-none bg-white"
      />

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button onClick={generate} disabled={busy || !summary.trim()}
          data-testid="habitability-generate-btn"
          className="h-10 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center gap-1.5 shadow-[3px_3px_0_#0f172a]">
          <FileText weight="fill" size={13}/>
          {busy ? "Generating…" : latest ? "Regenerate PDF" : "Generate PDF"}
        </button>
        {latest && (
          <a href={resolveUrl(latest.url)} target="_blank" rel="noreferrer"
            data-testid="habitability-latest-download"
            className="h-10 px-4 border-2 border-indigo-600 bg-white text-indigo-700 hover:bg-indigo-50 font-black uppercase text-xs tracking-widest rounded inline-flex items-center gap-1.5">
            <Download weight="bold" size={13}/> Download Latest
          </a>
        )}
        <span className="text-[10px] text-slate-500 ml-auto">
          Auto-copied to property record for L&amp;I audits
        </span>
      </div>

      {history.length > 1 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-indigo-700 flex items-center gap-1">
            <Clock weight="bold" size={11}/> Prior versions ({history.length - 1})
          </summary>
          <ul className="mt-1 space-y-0.5 text-[11px]">
            {history.slice(1).map((h) => (
              <li key={h.id} className="flex items-center gap-2">
                <span className="text-slate-500">{(h.generated_at || "").slice(0, 10)}</span>
                <span className="text-slate-400">·</span>
                <a href={resolveUrl(h.url)} target="_blank" rel="noreferrer"
                  className="text-indigo-700 hover:underline">
                  {h.generated_by || "download"}
                </a>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
